import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, RefreshCw, Info } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface ReExtractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: number;
  documentId: string;
  fileName: string;
  onSuccess: () => void;
}

const targetFieldOptions = [
  { id: "companyInfo.name", label: "Company Name" },
  { id: "companyInfo.uid", label: "Swiss UID (CHE-xxx.xxx.xxx)" },
  { id: "companyInfo.address", label: "Company Address" },
  { id: "taxInfo.canton", label: "Canton" },
  { id: "taxInfo.taxYear", label: "Tax Year" },
  { id: "taxInfo.vatNumber", label: "VAT Number" },
];

export function ReExtractDialog({
  open,
  onOpenChange,
  sessionId,
  documentId,
  fileName,
  onSuccess,
}: ReExtractDialogProps) {
  const [hints, setHints] = useState("");
  const [selectedFields, setSelectedFields] = useState<string[]>([
    "companyInfo.name",
    "companyInfo.uid",
  ]);

  const reExtractMutation = trpc.companyDiscovery.reExtractFile.useMutation({
    onSuccess: () => {
      toast.success("File re-extracted successfully");
      onSuccess();
    },
    onError: error => {
      if (error.data?.code === "BAD_REQUEST") {
        toast.error("Re-extraction unavailable", {
          description:
            "This file's original content is not available for re-extraction. Try uploading the file again.",
        });
      } else {
        toast.error("Re-extraction failed", {
          description: error.message,
        });
      }
    },
  });

  const handleSubmit = () => {
    reExtractMutation.mutate({
      sessionId,
      documentId,
      hints: hints.trim() || undefined,
      targetFields: selectedFields.length > 0 ? selectedFields : undefined,
    });
  };

  const toggleField = (fieldId: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldId)
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Re-Extract File
          </DialogTitle>
          <DialogDescription>
            Re-analyze <strong>{fileName}</strong> with custom instructions
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Feature Notice */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Re-extraction with hints helps the AI find specific fields that
              may have been missed. Provide context about where to look in the
              document.
            </AlertDescription>
          </Alert>

          {/* Target Fields */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              What are you looking for?
            </Label>
            <div className="space-y-2">
              {targetFieldOptions.map(field => (
                <div key={field.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={field.id}
                    checked={selectedFields.includes(field.id)}
                    onCheckedChange={() => toggleField(field.id)}
                  />
                  <Label
                    htmlFor={field.id}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Hints Input */}
          <div className="space-y-2">
            <Label htmlFor="hints" className="text-sm font-medium">
              Additional hints for the AI
            </Label>
            <Textarea
              id="hints"
              placeholder={`Example: "The company name is 'Habitusnet Consulting AG' and should appear in the document header. Look for UID in the format CHE-xxx.xxx.xxx near the company name."`}
              value={hints}
              onChange={e => setHints(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Describe where the information might be found or provide known
              values
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={reExtractMutation.isPending}>
            {reExtractMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Re-extracting...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-Extract with Hints
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
